import { useMemo, useState } from 'react'
import { format } from 'date-fns'
import { Building2, CalendarSearch, RotateCcw, Search } from 'lucide-react'
import { DateRangePicker } from '@/components/DateRangePicker'
import { DepartmentScopeFilter } from '@/components/department-scope-filter/DepartmentScopeFilter'
import { SortableHead, type SortDir } from '@/components/common/SortableHead'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { useDepartmentServiceRecordStatistics } from '@/hooks/useServiceRecords'
import { cn } from '@/lib/utils'
import { EmployeeDrilldownDrawer } from './components/EmployeeDrilldownDrawer'
import type { DepartmentScopeValue } from '@/types/department'
import type { ServiceRecordDepartmentStatRowItem } from '@/types/service-record'

/** 默认当月 */
function monthRange(): { start_date: string; end_date: string } {
  const now = new Date()
  return {
    start_date: format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd'),
    end_date: format(new Date(now.getFullYear(), now.getMonth() + 1, 0), 'yyyy-MM-dd'),
  }
}

export function DepartmentServiceStatisticsPage() {
  const [range, setRange] = useState<{ start_date?: string; end_date?: string }>(monthRange)
  const [applied, setApplied] = useState<{ start_date?: string; end_date?: string }>(monthRange)
  const [scopeValue, setScopeValue] = useState<DepartmentScopeValue>({ scope: 'my' })
  const [drawer, setDrawer] = useState<{ departmentId: number; departmentName: string } | null>(null)

  const { data, isLoading, isFetching } = useDepartmentServiceRecordStatistics({
    start_date: applied.start_date,
    end_date: applied.end_date,
    scope: scopeValue.scope,
    department_id: scopeValue.department_id,
  })

  const columns = data?.columns ?? []
  const rows = data?.rows ?? []
  const grandTotal = rows.reduce((sum, r) => sum + r.total, 0)

  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(null)

  // 客户端排序：未点击表头时保持后端顺序（服务次数降序、「未分配部门」垫底）；合计行固定底部不参与
  const sortedRows = useMemo(() => {
    const source = data?.rows ?? []
    if (!sort) return source
    const factor = sort.dir === 'asc' ? 1 : -1
    return [...source].sort((a, b) => {
      if (sort.key === 'name') {
        return (a.department_name || '').localeCompare(b.department_name || '', 'zh-Hans-CN') * factor
      }
      const value = (r: ServiceRecordDepartmentStatRowItem) =>
        sort.key === 'customers' ? r.customers : sort.key === 'total' ? r.total : r.counts[sort.key] ?? 0
      return (value(a) - value(b)) * factor
    })
  }, [data?.rows, sort])

  /** 点击表头：同列切换升降，异列按 firstDir 起排（数字列降序、名称列升序） */
  const handleSort = (key: string, firstDir: SortDir) =>
    setSort(prev => (prev?.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: firstDir }))
  // 只看我的范围内无部门视角，行不可下钻
  const drilldownEnabled = scopeValue.scope !== 'my'

  const reset = () => {
    const initial = monthRange()
    setRange(initial)
    setApplied(initial)
    setScopeValue({ scope: 'my' })
  }

  /** 点击部门行下钻员工明细；「未分配部门」不可点。 */
  const openDrilldown = (row: ServiceRecordDepartmentStatRowItem) => {
    if (!drilldownEnabled || row.department_id === 0) return
    setDrawer({ departmentId: row.department_id, departmentName: row.department_name })
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">部门服务统计</h1>
        <p className="mt-1 text-sm text-muted-foreground">按一级部门×服务类型统计服务量，点击部门行查看员工明细</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <DateRangePicker value={range} onChange={setRange} />
            <DepartmentScopeFilter
              value={{ scope: scopeValue.scope || 'my', department_id: scopeValue.department_id }}
              onChange={setScopeValue}
            />
            <div className="flex gap-2">
              <Button type="button" onClick={() => setApplied(range)} disabled={isFetching}>
                <Search className="mr-1.5 h-4 w-4" />
                查询
              </Button>
              <Button type="button" variant="outline" onClick={reset}>
                <RotateCcw className="mr-1.5 h-4 w-4" />
                重置
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2].map(i => (
                <Skeleton key={i} className="h-11 w-full" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <SortableHead className="sticky left-0 bg-background" active={sort?.key === 'name'} dir={sort?.dir ?? 'desc'} onSort={() => handleSort('name', 'asc')}>一级部门</SortableHead>
                    {columns.map(col => (
                      <SortableHead key={col.type_id} className="text-center" active={sort?.key === String(col.type_id)} dir={sort?.dir ?? 'desc'} onSort={() => handleSort(String(col.type_id), 'desc')}>{col.label}</SortableHead>
                    ))}
                    <SortableHead className="text-center" active={sort?.key === 'customers'} dir={sort?.dir ?? 'desc'} onSort={() => handleSort('customers', 'desc')}>服务客户</SortableHead>
                    <SortableHead className="text-center" active={sort?.key === 'total'} dir={sort?.dir ?? 'desc'} onSort={() => handleSort('total', 'desc')}>服务次数</SortableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRows.map(row => {
                    const clickable = drilldownEnabled && row.department_id !== 0
                    return (
                      <TableRow
                        key={row.department_id}
                        className={cn('transition-colors', clickable && 'cursor-pointer')}
                        onClick={() => openDrilldown(row)}
                      >
                        <TableCell className={cn('sticky left-0 bg-background font-medium', clickable && 'group-hover:bg-muted/60')}>
                          <span className="inline-flex items-center gap-1.5">
                            {row.department_id === 0 && <Building2 className="size-3.5 text-muted-foreground/50" />}
                            {row.department_name}
                          </span>
                        </TableCell>
                        {columns.map(col => {
                          const count = row.counts[String(col.type_id)] ?? 0
                          return (
                            <TableCell key={col.type_id} className={cn('text-center tabular-nums', count === 0 && 'text-muted-foreground/60')}>
                              {count}
                            </TableCell>
                          )
                        })}
                        <TableCell className="text-center tabular-nums">{row.customers}</TableCell>
                        <TableCell className="text-center font-semibold tabular-nums">{row.total}</TableCell>
                      </TableRow>
                    )
                  })}
                  {rows.length > 0 && (
                    <TableRow className="bg-muted/50 font-medium hover:bg-muted/50">
                      <TableCell className="sticky left-0 bg-muted/50">合计</TableCell>
                      {columns.map(col => (
                        <TableCell key={col.type_id} className="text-center tabular-nums">
                          {rows.reduce((sum, r) => sum + (r.counts[String(col.type_id)] ?? 0), 0)}
                        </TableCell>
                      ))}
                      <TableCell className="text-center tabular-nums">{data?.totals?.customers ?? '—'}</TableCell>
                      <TableCell className="text-center font-semibold tabular-nums">{data?.totals?.records ?? grandTotal}</TableCell>
                    </TableRow>
                  )}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columns.length + 3} className="py-12 text-center">
                        <div className="flex flex-col items-center gap-2 text-muted-foreground">
                          <CalendarSearch className="size-8 opacity-50" />
                          <p className="text-sm">所选范围内暂无服务记录</p>
                          <p className="text-xs">换个日期或数据范围试试</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <EmployeeDrilldownDrawer
        open={!!drawer}
        onOpenChange={open => !open && setDrawer(null)}
        departmentId={drawer?.departmentId ?? 0}
        departmentName={drawer?.departmentName ?? ''}
        dateRange={applied}
      />
    </div>
  )
}
